// Library methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme, Theme } from "@mui/material/styles";

// Hooks
import { useEMRHubProblems } from "src/services/emr/hooks/emr-problems/useEMRHubProblems";
import { useEMRHubConnections } from "src/services/emr/hooks/emr-connections/useEMRHubConnections";

// Utils
import { describeProblem } from "src/utils/emr/problemTextHelper";

// Styles
import {
  getRequiredErrorColor,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

const getCardBackground = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#00000090" : "rgba(255, 255, 255, 0.55)";

type HubProblemsType = {
  clinicId: string | undefined;
  hubId: string | undefined;
};

const HubProblems = ({ clinicId, hubId }: HubProblemsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText, onSurface } = getSurfaceTokens(theme);
  const errorColor = getRequiredErrorColor(theme);

  const { data: problems } = useEMRHubProblems(clinicId, hubId);
  const { data: connections } = useEMRHubConnections(clinicId, hubId);
  const sourceKindOf = (connectionId: string | null) =>
    connections?.find((connection) => connection.connectionId === connectionId)
      ?.sourceKind;

  if (!problems?.length) return null;

  return (
    <Box
      data-testid="hub-problems"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        backgroundColor: getCardBackground(theme),
        border: `1px solid ${errorColor}`,
        borderRadius: "8px",
        padding: "16px",
      }}
    >
      <Typography fontSize="16px" fontWeight={600} color={errorColor}>
        {t("Problems")} ({problems.length})
      </Typography>
      <Typography fontSize="12px" color={mutedText}>
        {t(
          "These need fixing on the clinic's side. Each one clears by itself once the hub reports it working again."
        )}
      </Typography>

      {problems.map((problem) => (
        <Box
          key={problem.problemId}
          data-testid="hub-problem"
          sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
        >
          <Typography fontSize="14px" fontWeight={600} color={onSurface}>
            {problem.connectionName ?? t("Hub")}
          </Typography>
          <Typography
            fontSize="14px"
            color={onSurface}
            sx={{ wordBreak: "break-word" }}
          >
            {describeProblem(problem, sourceKindOf(problem.connectionId), t)}
          </Typography>
          <Typography fontSize="12px" color={mutedText}>
            {t("Last seen")}: {new Date(problem.lastSeenAt).toLocaleString()}
            {problem.count > 1 && ` · ${t("Times seen")}: ${problem.count}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default HubProblems;
